import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AddOnActivationResult, AddOnBillingResult, AddOnCatalogItem, AddOnEnrollmentStatus, AddOnSavedPaymentMethod, StripePaymentSetupIntent } from '@app/core/models';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class AddonsApiService {
  private readonly http = inject(HttpClient);

  private baseUrl(accountId: string): string {
    return `${environment.apiBaseUrl}/accounts/${encodeURIComponent(accountId)}/add-ons`;
  }

  list(accountId: string) {
    return this.http.get<AddOnCatalogItem[]>(this.baseUrl(accountId));
  }

  paymentMethods(accountId: string) {
    return this.http.get<AddOnSavedPaymentMethod[]>(`${this.baseUrl(accountId)}/payment-methods`);
  }

  paymentSetup(accountId: string) {
    return this.http.post<StripePaymentSetupIntent>(`${this.baseUrl(accountId)}/decision-ai/payment-setup`, {});
  }

  activate(accountId: string, selectedPropertyIds: string[], paymentMethodId: string,
    setupIntentId: string | null, expectedTrialDays: number, idempotencyKey: string) {
    return this.http.post<AddOnActivationResult>(`${this.baseUrl(accountId)}/decision-ai/activate`,
      { selectedPropertyIds, paymentMethodId, setupIntentId, expectedTrialDays },
      { headers: new HttpHeaders({ 'Idempotency-Key': idempotencyKey }) });
  }

  status(accountId: string, accountAddOnId: string) {
    return this.http.get<AddOnEnrollmentStatus>(
      `${this.baseUrl(accountId)}/enrollments/${encodeURIComponent(accountAddOnId)}`
    );
  }

  checkout(accountId: string, selectedPropertyIds: string[], idempotencyKey: string) {
    return this.http.post<{ accountAddOnId: string; checkoutUrl: string }>(
      `${this.baseUrl(accountId)}/decision-ai/checkout`,
      { selectedPropertyIds },
      { headers: new HttpHeaders({ 'Idempotency-Key': idempotencyKey }) }
    );
  }

  changeProperties(accountId: string, accountAddOnId: string, selectedPropertyIds: string[],
    eTag: string, idempotencyKey: string) {
    return this.http.post<AddOnBillingResult>(
      `${this.baseUrl(accountId)}/enrollments/${encodeURIComponent(accountAddOnId)}/standalone-properties`,
      { selectedPropertyIds },
      { headers: new HttpHeaders({ 'If-Match': eTag, 'Idempotency-Key': idempotencyKey }) }
    );
  }

  cancel(accountId: string, accountAddOnId: string, eTag: string, idempotencyKey: string) {
    return this.http.post<AddOnBillingResult>(
      `${this.baseUrl(accountId)}/enrollments/${encodeURIComponent(accountAddOnId)}/standalone-cancellation`,
      {},
      { headers: new HttpHeaders({ 'If-Match': eTag, 'Idempotency-Key': idempotencyKey }) }
    );
  }

  reactivate(accountId: string, accountAddOnId: string, eTag: string, idempotencyKey: string) {
    return this.http.post<AddOnBillingResult>(
      `${this.baseUrl(accountId)}/enrollments/${encodeURIComponent(accountAddOnId)}/standalone-reactivation`,
      {},
      { headers: new HttpHeaders({ 'If-Match': eTag, 'Idempotency-Key': idempotencyKey }) }
    );
  }
}
